<template>
	<div class="chat-input-area" @click="focusInput">
		<!-- 拖拽覆盖层 -->
		<div v-if="isDragOver" class="drag-overlay">
			<div class="drag-content">
				<i class="el-icon-upload2"></i>
				<p>拖拽文件到此处自动发送</p>
				<p class="drag-tip">支持图片、文档等文件类型</p>
			</div>
		</div>

		<div :class="['edit-chat-container', isEmpty ? '' : 'not-empty']" contenteditable="true" @paste.prevent="onPaste"
			@keydown="onKeydown" @compositionstart="compositionFlag = true" @compositionend="onCompositionEnd"
			@input="onEditorInput" @mousedown="onMousedown" ref="content" @blur="onBlur"
			@focus="onInputFocus" @click.stop="onInputClick">
		</div>

		<!-- 🔥 引用消息指示器（放在输入框下方） -->
		<quoted-message
			v-if="quotedMessages && quotedMessages.length > 0"
			:messages="quotedMessages"
			@remove="handleRemoveQuote"
			@clear-all="handleClearAllQuotes"
			@open-detail="handleOpenQuoteDetail"
			class="input-quoted-message"
		/>

		<chat-at-box @select="onAtSelect" :search-text="atSearchText" ref="atBox" :ownerId="ownerId"
			:members="groupMembers"></chat-at-box>
		<!-- RooCode @ 面板 -->
		<roo-code-at-panel
			ref="rooCodePanel"
			:userId="ownerId"
			:targetUserId="targetUserId"
			:groupMembers="groupMembers"
			:isGroup="!!groupMembers && groupMembers.length > 0"
			:agents="agents"
		></roo-code-at-panel>
	</div>
</template>

<script>
import ChatAtBox from "./ChatAtBox";
import RooCodeAtPanel from "./RooCodeAtPanel";
import QuotedMessage from "./QuotedMessage.vue";
import VSCodeAdapter from '../../api/vscode-adapter.js';
import Emotion from '../common/Emotion.vue';

// 使用全局适配器实例
const globalAdapter = VSCodeAdapter;

export default {
	name: "ChatInput",
	components: { ChatAtBox, RooCodeAtPanel, QuotedMessage, Emotion },
	props: {
		ownerId: {
			type: Number,
		},
		targetUserId: {
			type: [Number, String],
			default: null
		},
		groupMembers: {
			type: Array,
		},
		// 新增：智能体列表
		agents: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			imageList: [],
			fileList: [],
			currentId: 0,
			atSearchText: null,
			compositionFlag: false,
			atIng: false,
			rooCodeAtIng: false,
			isEmpty: true,
			changeStored: true,
			blurRange: null,
			isDragOver: false,
			dragCounter: 0,
			globalDragListeners: null,
			// 智能体提及列表（用于消息发送）
			agentMentions: []
		}
	},
	mounted() {
		// 初始化全局适配器
		globalAdapter.init();
		
		// 添加全局拖拽事件监听器
		// 🔧 拖拽管理已迁移到父级EditorChat组件，这里不再设置全局监听器
		// this.addGlobalDragListeners();
	},
	beforeDestroy() {
		// 🔧 拖拽管理已迁移到父级EditorChat，这里不再处理
		// this.removeGlobalDragListeners();
	},
	methods: {
		// 🔥 引用消息处理方法
		handleRemoveQuote(messageId) {
			this.$store.commit('removeQuotedMessage', messageId);
		},
		handleClearAllQuotes() {
			this.$store.commit('clearQuotedMessages');
		},
		handleOpenQuoteDetail() {
			// 🔥 打开引用详情弹窗，使用与转发详情相同的组件
			this.$emit('open-quote-detail', this.quotedMessages);
		},

		/**
		 * 高性能安全设置innerHTML
		 */
		safeSetInnerHTML(element, html) {
			if (!element) return;
			
			// 直接使用全局适配器，避免重复检查
			globalAdapter.setInnerHTML(element, html);
		},

		/**
		 * 高性能安全获取innerHTML
		 */
		safeGetInnerHTML(element) {
			if (!element) return '';
			
			try {
				return element.innerHTML || '';
			} catch (error) {
				return element.textContent || '';
			}
		},
		
		onPaste(e) {
			this.isEmpty = false;
			
			// 🔥 只在真正为空且没有图片时清除初始内容
			const content = this.$refs.content;
			const textContent = content.textContent || '';
			const hasImages = content.querySelectorAll('img.chat-image').length > 0;
			if ((textContent.trim() === '' || textContent === '\u200B') && !hasImages) {
				content.innerHTML = '';
			}
			
			let txt = e.clipboardData.getData('Text')
			let range = window.getSelection().getRangeAt(0)
			if (range.startContainer !== range.endContainer || range.startOffset !== range.endOffset) {
				range.deleteContents();
			}
			
			// 粘贴图片和文件时，这里没有数据
			if (txt && typeof (txt) == 'string') {
				// 🔥 修复粘贴时的换行问题：将文本中的换行符转换为 <br> 标签
				const lines = txt.split('\n');
				const fragment = document.createDocumentFragment();

				lines.forEach((line, index) => {
					// 添加文本节点
					if (line) {
						fragment.appendChild(document.createTextNode(line));
					}
					// 如果不是最后一行，添加换行符
					if (index < lines.length - 1) {
						fragment.appendChild(document.createElement('br'));
					}
				});
				
				// 如果清空了内容，直接添加到容器
				if (content.innerHTML === '') {
					content.appendChild(fragment);
				} else {
					range.insertNode(fragment);
				}
				
				// 将光标移到插入内容的末尾
				range.collapse(false);
				return;
			}
			let items = (e.clipboardData || window.clipboardData).items
			if (items.length) {
				// 🔥 检查输入框是否为空（只有初始的空div或完全为空）
				const content = this.$refs.content;
				const hasImages = content.querySelectorAll('img.chat-image').length > 0;
				const textContent = content.textContent || '';
				const hasOnlyEmptyDiv = content.childNodes.length === 1 && 
					content.firstChild.nodeType === 1 && 
					content.firstChild.nodeName === 'DIV' &&
					(!content.firstChild.textContent || content.firstChild.textContent === '\u200B');
				const isCompletelyEmpty = content.childNodes.length === 0 || (textContent.trim() === '' && !hasImages);
				
				if ((hasOnlyEmptyDiv || isCompletelyEmpty) && !hasImages) {
					// 只在真正为空且没有图片时清空
					content.innerHTML = '';
				}
				
				for (let i = 0; i < items.length; i++) {
					if (items[i].type.indexOf('image') !== -1) {
						let file = items[i].getAsFile();
						let imagePush = {
							fileId: this.generateId(),
							file: file,
							url: URL.createObjectURL(file)
						};
						this.imageList[imagePush.fileId] = (imagePush);
						let imageElement = document.createElement('img');
						imageElement.className = 'chat-image no-text';
						imageElement.src = imagePush.url;
						imageElement.dataset.imgId = imagePush.fileId;
						range.insertNode(imageElement);
						// 将光标移到图片后面
						range.setStartAfter(imageElement);
						range.collapse(true);
					} else {
						let asFile = items[i].getAsFile();
						if (!asFile) {
							continue;
						}
						let filePush = { fileId: this.generateId(), file: asFile };
						this.fileList[filePush.fileId] = (filePush)
						
						// 🔥 直接插入文件
						let fileElement = this.createFile(filePush);
						content.appendChild(fileElement);
						
						// 设置光标到文件后
						let range = document.createRange();
						let selection = window.getSelection();
						range.setStartAfter(fileElement);
						range.collapse(true);
						selection.removeAllRanges();
						selection.addRange(range);
					}
				}
			}
		},
		selectElement(element, endOffset) {
			let selection = window.getSelection();
			// 插入元素可能不是立即执行的，vue可能会在插入元素后再更新dom
			this.$nextTick(() => {
				if (!element || !element.parentNode) {
					return;
				}
				try {
					let t1 = document.createRange();
					t1.setStart(element, 0);
					t1.setEnd(element, endOffset || 0);
					if (element.firstChild) {
						t1.selectNodeContents(element.firstChild);
					}
					t1.collapse();
					selection.removeAllRanges();
					selection.addRange(t1);
					this.$refs.content.focus();
				} catch (e) {
					// 忽略 range 错误
				}
			})
		},
		onCompositionEnd(e) {
			this.compositionFlag = false;
			this.onEditorInput(e);
		},
		onKeydown(e) {
			if (e.keyCode === 13) {
				e.preventDefault();
				e.stopPropagation();
				if (this.atIng) {
					console.log('选中at的人')
					this.$refs.atBox.select();
					return;
				}
				// Ctrl+Enter 或 Shift+Enter 换行
				if (e.ctrlKey || e.shiftKey) {
					let line = this.newLine();
					let after = document.createTextNode('\u00A0');
					line.appendChild(after);
					this.selectElement(line.childNodes[0], 0);
				} else {
					// 中文输入标记
					if (this.compositionFlag) {
						return;
					}
					this.submit();
				}
				return;
			}
			// 删除键
			if (e.keyCode === 8) {
				console.log("delete")
				// 等待dom更新
				setTimeout(() => {
					let s = this.safeGetInnerHTML(this.$refs.content).trim();
					// 空dom时，需要刷新dom
					console.log(s);
					if (s === '' || s === '<br>' || s === '<div>&nbsp;</div>') {
						// 拼接随机长度的空格，以刷新dom
						this.empty();
						this.isEmpty = true;
						this.selectElement(this.$refs.content);
					} else {
						this.isEmpty = false;
					}
				})
			}
			// at框打开时，上下键移动特殊处理
			if (this.atIng) {
				if (e.keyCode === 38) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveUp();
				}
				if (e.keyCode === 40) {
					e.preventDefault();
					e.stopPropagation();
					this.$refs.atBox.moveDown();
				}
			}

		},
		onAtSelect(member) {
			this.atIng = false;
			// 选中输入的 @xx 符
			let blurRange = this.blurRange;
			let endContainer = blurRange.endContainer
			let startOffset = endContainer.data.indexOf("@" + this.atSearchText);
			let endOffset = startOffset + this.atSearchText.length + 1;
			blurRange.setStart(blurRange.endContainer, startOffset);
			blurRange.setEnd(blurRange.endContainer, endOffset);
			blurRange.deleteContents()
			blurRange.collapse();
			console.log("onAtSelect")
			this.focus();
			// 创建元素节点
			let element = document.createElement('SPAN')
			element.className = "chat-at-user";
			element.dataset.id = member.userId;
			element.contentEditable = 'false'
			element.innerText = `@${member.showNickName}`
			blurRange.insertNode(element)
			// 光标移动到末尾
			blurRange.collapse()

			// 插入空格
			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode);

			blurRange.collapse()
			this.atSearchText = "";
			this.selectElement(textNode, 1);
		},
		onEditorInput(e) {
			this.isEmpty = false;
			this.changeStored = false;
			
			// 检查内容是否溢出，动态显示滚动条
			this.checkOverflow();
			
			// 检测 @ 符号
			if (!this.compositionFlag) {
				let selection = window.getSelection()
				let range = selection.getRangeAt(0);
				// 截取@后面的名称作为过滤条件，并以空格结束
				let endContainer = range.endContainer;
				let endOffset = range.endOffset;
				let textContent = endContainer.textContent;
				let startIndex = -1;
				for (let i = endOffset; i >= 0; i--) {
					if (textContent[i] === '@') {
						startIndex = i;
						break;
					}
				}
				
				// 没有at符号，则关闭弹窗
				if (startIndex === -1) {
					this.$refs.atBox.close();
					this.$refs.rooCodePanel.close();
					this.rooCodeAtIng = false;
					return;
				}

				// 检查@符号前是否有内容（判断是否是输入开头）
				let beforeAtText = textContent.substring(0, startIndex).trim();
				let isAtStart = beforeAtText.length === 0;
				
				let endIndex = endOffset;
				for (let i = endOffset; i < textContent.length; i++) {
					if (textContent[i] === ' ') {
						endIndex = i;
						break;
					}
				}
				this.atSearchText = textContent.substring(startIndex + 1, endIndex).trim();
				
				// 显示RooCode面板（无论是否有搜索文本）
				// 面板会根据搜索文本自动过滤内容
				if (!this.rooCodeAtIng) {
					this.showRooCodePanel(e);
				}
			}
		},
		onBlur(e) {
			this.updateRange();

		},
		onInputFocus() {
			// 触发事件通知父组件隐藏智能体侧面板
			this.$emit('input-focus');
		},
		onInputClick(e) {
			// 检查是否点击了图片
			if (e && e.target && e.target.tagName === 'IMG' && e.target.classList.contains('chat-image')) {
				const imgId = e.target.dataset.imgId;
				const imageData = this.imageList[imgId];
				if (imageData && imageData.url) {
					// 使用和聊天消息一样的图片预览
					this.$store.commit('showFullImageBox', imageData.url);
				}
				return;
			}
			// 触发事件通知父组件隐藏智能体侧面板
			this.$emit('input-click');
		},
		onMousedown() {
			if (this.atIng) {
				this.$refs.atBox.close();
				this.atIng = false;
			}
			if (this.rooCodeAtIng) {
				this.$refs.rooCodePanel.close();
				this.rooCodeAtIng = false;
			}
		},
		// RooCode面板相关方法
		showRooCodePanel(e) {
			this.rooCodeAtIng = true;
			let selection = window.getSelection()
			let range = selection.getRangeAt(0)
			
			// 获取光标位置，如果位置无效则使用输入框位置
			let pos = range.getBoundingClientRect();
			
			// 检查位置是否有效（避免出现在左上角）
			if (!pos || pos.x <= 0 || pos.y <= 0 || pos.width === 0 || pos.height === 0) {
				// 使用输入容器的位置作为备选
				const inputContainer = this.$refs.content;
				if (inputContainer) {
					const containerRect = inputContainer.getBoundingClientRect();
					pos = {
						x: containerRect.left + 20,
						y: containerRect.top
					};
				} else {
					pos = { x: 100, y: 100 };
				}
			}
			
			this.$refs.rooCodePanel.open({
				x: pos.x,
				y: pos.y
			}, this.onRooCodeSelect.bind(this), null, this.atSearchText);
			// 记录光标所在位置
			this.updateRange();
		},
		onRooCodeSelect(result) {
			this.rooCodeAtIng = false;
			// 选中输入的 @ 符
			let blurRange = this.blurRange;
			let endContainer = blurRange.endContainer;

			// 检查endContainer.data是否存在，避免undefined错误
			if (endContainer && endContainer.data && typeof endContainer.data === 'string') {
				let startOffset = endContainer.data.indexOf("@");
				if (startOffset >= 0) {
					let endOffset = startOffset + 1;
					blurRange.setStart(blurRange.endContainer, startOffset);
					blurRange.setEnd(blurRange.endContainer, endOffset);
					blurRange.deleteContents();
					blurRange.collapse();
				}
			} else {
				try {
					blurRange.deleteContents();
					blurRange.collapse();
				} catch (e) {
					// 静默处理DOM操作异常
				}
			}

			this.focus();

			// 根据类型创建不同的元素
			let element;
			if (result.type === 'members') {
				element = document.createElement('SPAN')
				element.className = "chat-at-user";
				element.dataset.id = result.item.userId;
				element.contentEditable = 'false'
				element.innerText = result.displayText;
			} else {
				element = document.createElement('SPAN')
				element.className = "chat-at-user";
				element.dataset.agentId = result.item.agentId;
				element.dataset.agentName = result.item.agentName;
				element.contentEditable = 'false'
				element.innerText = result.displayText;

				// 记录智能体提及
				this.agentMentions.push({
					agentId: result.item.agentId,
					agentName: result.item.agentName,
					description: result.item.description,
					avatarUrl: result.item.avatarUrl
				});
			}

			blurRange.insertNode(element)
			blurRange.collapse()

			// 插入空格
			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode);

			blurRange.collapse()
			this.selectElement(textNode, 1);
		},
		insertEmoji(emojiText) {
			let emojiElement = document.createElement('img');
			emojiElement.className = 'emoji-normal no-text';
			emojiElement.dataset.emojiCode = emojiText;
			emojiElement.src = this.$emo.textToUrl(emojiText);

			let blurRange = this.blurRange;
			if (!blurRange) {
				this.focus();
				this.updateRange();
				blurRange = this.blurRange;
			}
			if (blurRange.startContainer !== blurRange.endContainer || blurRange.startOffset !== blurRange.endOffset) {
				blurRange.deleteContents();
			}
			blurRange.insertNode(emojiElement);
			blurRange.collapse()

			let textNode = document.createTextNode('\u00A0');
			blurRange.insertNode(textNode)
			blurRange.collapse()

			this.selectElement(textNode);
			this.updateRange();
			this.isEmpty = false;
		},
		generateId() {
			return this.currentId++;
		},
		createFile(filePush) {
			let file = filePush.file;
			let fileId = filePush.fileId;
			let container = document.createElement('div');
			container.className = 'chat-file-container no-text';
			container.contentEditable = 'false';
			container.dataset.fileId = fileId;

			let left = document.createElement('div');
			left.className = 'file-position-left';
			container.appendChild(left);

			let icon = document.createElement('div');
			icon.className = 'el-icon-document';
			left.appendChild(icon);

			let right = document.createElement('div');
			right.className = 'file-position-right';
			container.appendChild(right);

			let fileName = document.createElement('div');
			fileName.className = 'file-name';
			fileName.innerText = file.name;

			let fileSize = document.createElement('div');
			fileSize.className = 'file-size';
			fileSize.innerText = this.sizeConvert(file.size);

			right.appendChild(fileName);
			right.appendChild(fileSize);

			return container;
		},
		sizeConvert(len) {
			if (len < 1024) {
				return len + 'B';
			} else if (len < 1024 * 1024) {
				return (len / 1024).toFixed(2) + 'KB';
			} else if (len < 1024 * 1024 * 1024) {
				return (len / 1024 / 1024).toFixed(2) + 'MB';
			} else {
				return (len / 1024 / 1024 / 1024).toFixed(2) + 'GB';
			}
		},
		updateRange() {
			let selection = window.getSelection();
			this.blurRange = selection.getRangeAt(0);
		},
		newLine() {
			let selection = window.getSelection();
			let range = selection.getRangeAt(0);
			let divElement = document.createElement('div');
			let endContainer = range.endContainer;
			let parentElement = endContainer.parentElement;
			if (parentElement.parentElement === this.$refs.content) {
				this.safeSetInnerHTML(divElement, endContainer.textContent.substring(range.endOffset).trim());
				endContainer.textContent = endContainer.textContent.substring(0, range.endOffset);
				// 插入到当前div（当前行）后面
				parentElement.insertAdjacentElement('afterend', divElement);
			} else {
				this.safeSetInnerHTML(divElement, '');
				this.$refs.content.append(divElement);
			}
			return divElement;
		},
		clear() {
			this.empty();
			this.imageList = [];
			this.fileList = [];
		},
		empty() {
			this.safeSetInnerHTML(this.$refs.content, "");
			// 修复：清空后直接创建新的div，不依赖当前选区
			let divElement = document.createElement('div');
			// 使用零宽空格，不可见但可以定位光标
			let after = document.createTextNode('\u200B');
			divElement.appendChild(after);
			this.$refs.content.append(divElement);

			this.$nextTick(() => this.selectElement(after));
		},
		showAtBox(e) {
			this.atIng = true;
			// show之后会自动更新当前搜索的text
			// this.atSearchText = "";
			let selection = window.getSelection()
			let range = selection.getRangeAt(0)
			// 光标所在坐标
			let pos = range.getBoundingClientRect();
			this.$refs.atBox.open({
				x: pos.x,
				y: pos.y
			})
			// 记录光标所在位置
			this.updateRange();
		},
		html2Escape(strHtml) {
			return strHtml.replace(/[<>&"]/g, function (c) {
				return {
					'<': '&lt;',
					'>': '&gt;',
					'&': '&amp;',
					'"': '&quot;'
				}[c];
			});
		},
		submit() {
			console.log('[ChatInput] 📤 开始提交消息...');
			
			let nodes = this.$refs.content.childNodes;
			let fullList = [];
			let tempText = '';
			let atUserIds = [];
			
			let each = (nodes) => {
				for (let i = 0; i < nodes.length; i++) {
					let node = nodes[i];
					if (!node) {
						continue;
					}
					if (node.nodeType === 3) {
						tempText += this.html2Escape(node.textContent);
						continue;
					}
					let nodeName = node.nodeName.toLowerCase();
					if (nodeName === 'script') {
						continue;
					}
					let text = tempText.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
					if (nodeName === 'img') {
						let imgId = node.dataset.imgId;
						if (imgId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: [...atUserIds] // 复制数组避免引用问题
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'image',
								content: this.imageList[imgId]
							})
						} else {
							let emojiCode = node.dataset.emojiCode;
							tempText += emojiCode;
						}
					} else if (nodeName === 'div') {
						let fileId = node.dataset.fileId
						// 文件
						if (fileId) {
							if (text) {
								fullList.push({
									type: 'text',
									content: text,
									atUserIds: [...atUserIds] // 复制数组避免引用问题
								})
								tempText = '';
								atUserIds = []
							}
							fullList.push({
								type: 'file',
								content: this.fileList[fileId]
							})
						} else {
							tempText += '\n';
							each(node.childNodes);
						}
					} else if (nodeName === 'br') {
						// 🔥 处理换行符：粘贴时插入的 <br> 标签
						tempText += '\n';
					} else if (nodeName === 'span') {
						if (node.classList && node.classList.contains('chat-at-user')) {
							tempText += node.innerText || node.textContent;
							if (node.dataset.id) {
								atUserIds.push(node.dataset.id)
							}
							if (node.dataset.agentId) {
								this.agentMentions.push({
									agentId: node.dataset.agentId,
									agentName: node.dataset.agentName
								});
							}
						} else if(node.outerHTML) {
							tempText += node.outerHTML;
						}
					}
				}
			}
			
			each(nodes);
			
			// 移除零宽字符后再判断
			let text = tempText.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
			if (text !== '') {
				const textMessage = {
					type: 'text',
					content: text,
					atUserIds: [...atUserIds]
				};
				// 添加智能体提及信息
				if (this.agentMentions.length > 0) {
					textMessage.agentMentions = [...this.agentMentions];
				}
				fullList.push(textMessage);
			}

			console.log('[ChatInput] 📦 构建的消息列表:', {
				count: fullList.length,
				hasAgentMentions: this.agentMentions.length > 0,
				messages: fullList.map(m => ({ type: m.type, hasContent: !!m.content }))
			});

			if (fullList.length === 0) {
				console.warn('[ChatInput] ⚠️ 没有内容可以发送');
				return;
			}

			this.$emit('submit', fullList);

			// 清空输入框和状态
			this.empty();
			this.isEmpty = true;
			this.agentMentions = [];
		},
		focus() {
			this.$refs.content.focus();
		},
		focusInput(e) {
			// 点击空白区域时聚焦到输入框
			if (e.target.classList.contains('chat-input-area')) {
				this.$refs.content.focus();
			}
		},
		checkOverflow() {
			// 检查内容是否溢出
			const el = this.$refs.content;
			if (el) {
				if (el.scrollHeight > el.clientHeight) {
					el.classList.add('has-overflow');
				} else {
					el.classList.remove('has-overflow');
				}
			}
		},
		// 🔧 拖拽事件处理已迁移到父级EditorChat组件统一管理
		// ChatInput专注于业务逻辑，不再直接处理DOM拖拽事件
		handleDroppedFiles(files) {
			if (!files || files.length === 0) {
				return;
			}
			
			const processedFiles = [];
			
			// 处理每个文件
			for (let i = 0; i < files.length; i++) {
				const file = files[i];
				
				// 检查文件大小限制（例如：500MB）
				const maxSize = 500 * 1024 * 1024; // 500MB
				if (file.size > maxSize) {
					this.$message.error(`文件 "${file.name}" 超过100MB大小限制`);
					continue;
				}
				
				console.log('处理文件:', file.name, '类型:', file.type, '大小:', this.sizeConvert(file.size));
				
				// 判断文件类型并处理
				if (this.isImageFile(file)) {
					// 图片文件处理
					const imagePush = {
						fileId: this.generateId(),
						file: file,
						url: URL.createObjectURL(file)
					};
					this.imageList[imagePush.fileId] = imagePush;
					processedFiles.push({
						type: 'image',
						content: imagePush
					});
				} else {
					// 普通文件处理
					const filePush = {
						fileId: this.generateId(),
						file: file
					};
					this.fileList[filePush.fileId] = filePush;
					processedFiles.push({
						type: 'file',
						content: filePush
					});
				}
			}
			
			// 自动发送拖拽的文件
			if (processedFiles.length > 0) {
				console.log('自动发送拖拽文件:', processedFiles);
				
				// 触发提交事件
				this.$emit('submit', processedFiles);
				
				// 显示成功提示
				const fileCount = processedFiles.length;
				const imageCount = processedFiles.filter(f => f.type === 'image').length;
				const documentCount = processedFiles.filter(f => f.type === 'file').length;
				
				let message = `已发送${fileCount}个文件`;
				if (imageCount > 0 && documentCount > 0) {
					message = `已发送${imageCount}张图片和${documentCount}个文档`;
				} else if (imageCount > 0) {
					message = `已发送${imageCount}张图片`;
				} else if (documentCount > 0) {
					message = `已发送${documentCount}个文档`;
				}
				
				this.$message.success(message);
				
				// 清空输入区域（可选）
				// this.clear();
			} else {
				this.$message.warning('没有有效的文件可以发送');
			}
		},
		isImageFile(file) {
			const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
			return imageTypes.includes(file.type) || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name);
		},

		setCursorToEnd(element) {
			const range = document.createRange();
			const selection = window.getSelection();
			range.selectNodeContents(element);
			range.collapse(false);
			selection.removeAllRanges();
			selection.addRange(range);
		},

		// 🔧 拖拽监听器管理已迁移到父级EditorChat组件
		// 这里保留文件处理业务逻辑，供父级调用
	},
	computed: {
		// 🔥 获取当前引用的消息列表
		quotedMessages() {
			const quotedState = this.$store.state.chatStore.quotedMessages;
			if (!quotedState || !quotedState.active) {
				return [];
			}
			return quotedState.messages || [];
		}
	},
	watch: {
		// 监听引用消息变化
		quotedMessages(newVal) {
			console.log('[ChatInput] 引用消息更新:', newVal);
		}
	},
	created() {
	}
}
</script>

<style lang="scss">
.chat-input-area {
	width: 100%;
	height: 100%;
	position: relative;
	overflow-y: auto;
	overflow-x: hidden;
	cursor: text;
	max-width: 100%;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;

	// 🔥 输入框中的引用面板样式（靠左）
	.input-quoted-message.quoted-message-wrapper,
	.input-quoted-message .quoted-message-wrapper {
		text-align: left !important;
		margin-left: 8px;
	}

	.edit-chat-container {
		width: 100%;
		max-width: 100%;
		outline: none;
		padding: 10px;
		line-height: 1.5;
		font-size: var(--im-font-size);
		text-align: left;
		overflow-y: hidden;
		overflow-x: hidden;
		overflow-wrap: anywhere;
		word-break: break-all;
		white-space: pre-wrap;
		cursor: text;
		box-sizing: border-box;
		
		/* 移除内部自动创建的div的margin和padding */
		> div {
			margin: 0;
			padding: 0;
			line-height: inherit;
		}
		
		/* 移除空div的高度 */
		> div:empty {
			display: inline;
			height: 0;
		}
		
		/* 移除只包含零宽字符的div */
		> div:empty::before {
			content: none;
		}
		
		/* 内容超出时自动显示滚动条 */
		&:focus,
		&.has-overflow {
			overflow-y: auto;
		}
		
		&::-webkit-scrollbar {
			width: 6px;
		}
		
		&::-webkit-scrollbar-track {
			background: #f1f1f1;
		}
		
		&::-webkit-scrollbar-thumb {
			background: #ccc;
			border-radius: 3px;
		}
		
		&::-webkit-scrollbar-thumb:hover {
			background: #999;
		}
		
		// 任务续问状态下的placeholder样式
		&[data-placeholder="继续提问..."]:after {
			content: attr(data-placeholder);
			color: #999;
			font-style: italic;
			pointer-events: none;
			position: absolute;
			top: 28px;
			left: 5px;
			z-index: 1;
		}

		.chat-image {
			display: inline;
			max-width: 200px;
			max-height: 100px;
			border: 1px solid #e6e6e6;
			cursor: pointer;
			vertical-align: bottom;
		}

		.chat-file-container {
			max-width: 65%;
			padding: 10px;
			border: 2px solid #587ff0;
			display: flex;
			background: #eeeC;
			border-radius: 10px;

			.file-position-left {
				display: flex;
				width: 80px;
				justify-content: center;
				align-items: center;

				.el-icon-document {
					font-size: 40px;
					text-align: center;
					color: #d42e07;
				}
			}

			.file-position-right {
				flex: 1;

				.file-name {
					font-size: 16px;
					font-weight: 600;
					color: #66b1ff;
				}

				.file-size {
					font-size: 14px;
					font-weight: 600;
				}
			}
		}

		.chat-at-user {
			color: #00f;
			border-radius: 3px;
		}
	}

	.edit-chat-container>div:nth-of-type(1):after {
		content: '请输入消息（按Enter键发送 Shift+Enter键换行）';
		color: gray;
		position: absolute;
		top: 10px;
		left: 10px;
		pointer-events: none;
	}

	.edit-chat-container.not-empty>div:nth-of-type(1):after {
		content: none;
	}

	.drag-overlay {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.5);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 999;

		.drag-content {
			background-color: white;
			padding: 20px;
			border-radius: 10px;
			text-align: center;
			box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

			i {
				font-size: 40px;
				color: #587ff0;
				margin-bottom: 10px;
				display: block;
			}

			p {
				margin: 5px 0;
				font-size: 16px;
				color: #333;
			}

			.drag-tip {
				font-size: 14px;
				color: #666;
				margin-top: 8px;
			}
		}
	}

}
</style>
